import { flushSync } from "react-dom";
import { anchorToggle } from "@/lib/anchor-toggle";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { resetBreakingFree } from "@/lib/breaking-free.functions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { withMemberGate } from "@/lib/with-member-gate";
import {
  CLOSING_BLOCKS,
  DAYS,
  INTRO_SECTIONS,
  NO_STREAK_NOTICE,
  PHASES,
  phaseForDay,
} from "@/lib/breaking-free";
import { BreakingFreeProvider, useBreakingFree } from "@/components/breaking-free/state";
import { BlockRenderer } from "@/components/breaking-free/Blocks";
import { ProgressBar, SafetyNote } from "@/components/breaking-free/Shared";

export const Route = createFileRoute("/breaking-free/")({
  head: () => ({
    meta: [
      { title: "Breaking Free — 30-Day Trauma Bond Recovery Journey | Recovery Trauma™" },
      {
        name: "description",
        content:
          "A self-paced, trauma-informed 30-day journey to understand the bond, recognise patterns, regulate difficult emotions and rebuild self-trust.",
      },
      {
        property: "og:title",
        content: "Breaking Free — 30-Day Trauma Bond Recovery Journey",
      },
      {
        property: "og:description",
        content:
          "Understand the bond, recognise your patterns, regulate difficult emotions and begin moving forward — at your own pace.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: withMemberGate(BreakingFreePage, "Breaking Free"),
});

function BreakingFreePage() {
  return (
    <BreakingFreeProvider>
      <Inner />
    </BreakingFreeProvider>
  );
}

function Inner() {
  const { loading, error, refetch, completedDays, currentDay, progress, begin, activityByDay } =
    useBreakingFree();
  const started = Boolean(progress);
  const [tab, setTab] = useState<"journey" | "before" | "after">(
    started ? "journey" : "before",
  );
  const finished = completedDays.length >= 30;

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-16 text-center">
        <p className="text-base">Something didn't load correctly.</p>
        <Button className="mt-4 min-h-[48px]" onClick={refetch}>
          Try again
        </Button>
      </div>
    );
  }

  const current = DAYS.find((d) => d.day === currentDay) ?? DAYS[0];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-8 sm:pb-16 sm:pt-12">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
        30-day guided journey
      </p>
      <h1 className="mt-2 text-3xl font-semibold sm:text-4xl">💔 Breaking Free</h1>
      <p className="mt-1 text-lg text-muted-foreground">
        A 30-Day Trauma Bond Recovery Journey
      </p>
      <p className="mt-4 text-[15px] leading-relaxed text-foreground/85">
        A self-paced journey to understand the bond, recognise patterns, regulate difficult
        emotions, rebuild self-trust and begin moving forward. Written by Jana from her book of the
        same name.
      </p>
      {!started ? (
        <p className="mt-4 rounded-2xl border border-gold/40 bg-gold-light/25 px-4 py-3 text-sm font-medium text-[var(--gold-deep)]">
          New here? Please read <span className="font-semibold">Before you begin</span> below first
          — it gently prepares you for the journey.
        </p>
      ) : null}

      {/* Where you are — the member's current day, first and prominent */}
      <div className="mt-6 rounded-3xl border border-gold/40 bg-gold-light/25 p-5 sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
          Day {current.day} of 30
        </p>
        <h2 className="mt-2 text-xl font-semibold leading-snug sm:text-2xl">
          {current.emoji} {current.title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground/80">
          {current.focus[0] ??
            "Continue your journey — one gentle day at a time, at your own pace."}
        </p>
        <div className="mt-4">
          <ProgressBar
            done={completedDays.length}
            total={30}
            inProgress={
              Object.keys(activityByDay).filter((d) => !completedDays.includes(Number(d))).length
            }
            activities={Object.values(activityByDay).reduce((a, b) => a + b, 0)}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button asChild className="min-h-[48px]">
            <Link to="/breaking-free/day/$day" params={{ day: String(currentDay) }}>
              {completedDays.length === 0 ? "Start the journey" : `Continue Day ${current.day}`}
            </Link>
          </Button>
          <Button asChild variant="outline" className="min-h-[48px]">
            <Link to="/breaking-free/journey">View my reflections</Link>
          </Button>
          {!started ? (
            <Button variant="ghost" className="min-h-[48px]" onClick={() => void begin()}>
              Save my place
            </Button>
          ) : null}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{NO_STREAK_NOTICE}</p>
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto pb-1">
        {(
          [
            ["before", "Before you begin"],
            ["journey", "The 30 days"],
            ["after", "Your journey continues"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`min-h-[44px] whitespace-nowrap rounded-full border px-4 text-sm ${
              tab === key
                ? "border-gold bg-gold-light/50 font-semibold text-[var(--gold-deep)]"
                : "border-border bg-background text-foreground/80"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-8">
        {tab === "journey" ? <JourneyGrid completedDays={completedDays} currentDay={currentDay} /> : null}
        {tab === "before" ? <BeforeYouBegin /> : null}
        {tab === "after" ? <AfterThirty finished={finished} /> : null}
      </div>

      <div className="mt-10">
        <SafetyNote />
      </div>

      {started ? <ResetJourney onReset={() => refetch()} /> : null}

      {/* Sticky continue button — a returning member never has to hunt for their day */}
      {tab === "journey" && !finished ? (
        <div className="fixed inset-x-0 bottom-[76px] z-40 px-4 sm:hidden">
          <div className="rounded-2xl border border-border/60 bg-background/95 p-2 shadow-lg backdrop-blur">
          <Button asChild className="min-h-[48px] w-full text-base">
            <Link to="/breaking-free/day/$day" params={{ day: String(currentDay) }}>
              Continue Day {currentDay} <span className="inline-block rtl:rotate-180">→</span>
            </Link>
          </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const PHASE_EMOJIS: Record<string, string> = {
  awareness: "🌱",
  cycle: "🔄",
  resilience: "💛",
  beginnings: "🌅",
};

function JourneyGrid({
  completedDays,
  currentDay,
}: {
  completedDays: number[];
  currentDay: number;
}) {
  const currentPhase = phaseForDay(currentDay);
  // On mobile the member's current phase starts open; the rest stay collapsed.
  const [open, setOpen] = useState<string | null>(currentPhase.key);

  return (
    <div className="space-y-4">
      {PHASES.map((phase, index) => {
        const days = DAYS.filter((d) => d.day >= phase.from && d.day <= phase.to);
        const explored = days.filter((d) => completedDays.includes(d.day)).length;
        const isOpen = open === phase.key;
        return (
          <section key={phase.key} className="rounded-3xl border border-border bg-background">
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={(e) => { const t = e.currentTarget; anchorToggle(t, () => flushSync(() => setOpen(isOpen ? null : phase.key))); }}
              className="flex min-h-[64px] w-full items-center justify-between gap-3 px-5 py-4 text-start"
            >
              <span className="min-w-0">
                <span className="block text-base font-semibold">
                  {PHASE_EMOJIS[phase.key]} Phase {index + 1} — {phase.name}
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  {phase.range}
                  {explored > 0 ? ` · ${explored} of ${days.length} explored` : ""}
                </span>
              </span>
              <span className="shrink-0 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground/80">
                {isOpen ? "Hide" : "View days"}
              </span>
            </button>
            {isOpen ? (
              <div className="px-4 pb-5">
                <p className="text-sm text-foreground/80">{phase.blurb}</p>
                <ul className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {days.map((d) => {
                    const done = completedDays.includes(d.day);
                    const current = d.day === currentDay;
                    return (
                      <li key={d.day}>
                        <Link
                          to="/breaking-free/day/$day"
                          params={{ day: String(d.day) }}
                          className={`flex min-h-[64px] items-center gap-3 rounded-2xl border p-4 transition ${
                            done
                              ? "border-emerald-300/70 bg-emerald-50/50 dark:bg-emerald-950/20"
                              : current
                                ? "border-gold bg-gold-light/40"
                                : "border-border bg-background hover:bg-muted"
                          }`}
                        >
                          <span
                            className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold ${
                              done
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                                : current
                                  ? "bg-gold-light text-[var(--gold-deep)]"
                                  : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {done ? "✓" : current ? "●" : "○"}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Day {d.day}
                            </span>
                            <span className="block truncate text-sm font-medium leading-snug">
                              {d.emoji} {d.title}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {done ? (
                                "Completed"
                              ) : current ? (
                                <>
                                  Continue{" "}
                                  <span className="inline-block rtl:rotate-180">→</span>
                                </>
                              ) : (
                                ""
                              )}
                            </span>
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function ResetJourney({ onReset }: { onReset: () => void }) {
  const resetFn = useServerFn(resetBreakingFree);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!confirming) {
    return (
      <div className="mt-8 text-center">
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="min-h-[44px] px-4 text-sm text-muted-foreground underline underline-offset-4"
        >
          Start over from Day 1
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-3xl border border-border bg-background p-5">
      <h3 className="text-base font-semibold">Start over from Day 1?</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground/85">
        This will permanently delete your Breaking Free progress and everything you have written in
        the journey — every reflection, every answer, all 30 days. This cannot be undone.
      </p>
      {failed ? (
        <p className="mt-2 text-sm text-destructive">
          Something went wrong — nothing was deleted. Please try again.
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          variant="destructive"
          className="min-h-[48px]"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setFailed(false);
            void resetFn()
              .then(() => {
                setConfirming(false);
                onReset();
              })
              .catch(() => setFailed(true))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? "Deleting…" : "Yes, delete everything and start over"}
        </Button>
        <Button
          variant="outline"
          className="min-h-[48px]"
          disabled={busy}
          onClick={() => setConfirming(false)}
        >
          Keep my journey
        </Button>
      </div>
    </div>
  );
}

function BeforeYouBegin() {
  const [open, setOpen] = useState<string | null>(INTRO_SECTIONS[0]?.key ?? null);
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        The learning and preparation chapters from the book. Read them in any order — or come back
        to them later.
      </p>
      {INTRO_SECTIONS.map((section) => (
        <div key={section.key} className="rounded-3xl border border-border bg-background">
          <button
            type="button"
            className="flex min-h-[56px] w-full items-center justify-between gap-3 px-5 text-start"
            onClick={(e) => { const t = e.currentTarget; anchorToggle(t, () => flushSync(() => setOpen(open === section.key ? null : section.key))); }}
          >
            <span className="text-sm font-semibold">
              {section.emoji} {section.title}
            </span>
            <span className="text-muted-foreground">{open === section.key ? "–" : "+"}</span>
          </button>
          {open === section.key ? (
            <div className="space-y-4 px-4 pb-5">
              {section.blocks.map((block) => (
                <BlockRenderer key={block.key} block={block} day={null} />
              ))}
              {section.safety ? (
                <p className="rounded-2xl bg-muted/50 p-3 text-xs text-muted-foreground">
                  {section.safety}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function AfterThirty({ finished }: { finished: boolean }) {
  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-gold/30 bg-gold-light/25 p-5">
        <h2 className="text-lg font-semibold">30 days explored. Your healing doesn't have a deadline.</h2>
        <p className="mt-2 text-sm text-foreground/80">
          {finished
            ? "You've walked through all thirty days. Nothing closes — everything here stays yours."
            : "This part is always open, whether you're on Day 3 or Day 30. Nothing is locked."}
        </p>
      </div>
      {CLOSING_BLOCKS.map((block) => (
        <BlockRenderer key={block.key} block={block} day={null} />
      ))}
      <div className="flex flex-wrap gap-3">
        {[
          { to: "/grounding", label: "Continue regulating" },
          { to: "/trigger-journal", label: "My Trigger Journal" },
          { to: "/journal", label: "My Recovery Journal" },
          { to: "/affirmations", label: "My affirmations" },
        ].map((l) => (
          <Button key={l.to} asChild variant="outline" className="min-h-[48px]">
            <Link to={l.to as never}>{l.label}</Link>
          </Button>
        ))}
        <Button asChild variant="outline" className="min-h-[48px]">
          <Link to="/breaking-free/journey">My Breaking Free Journey</Link>
        </Button>
      </div>

      <div className="rounded-3xl border border-border bg-background p-5">
        <h3 className="text-base font-semibold">
          Want to understand the attachment patterns underneath the bond?
        </h3>
        <p className="mt-2 text-sm text-foreground/85">
          🤍 Secure Within is a 30-day journey into attachment, triggers, needs and more secure ways
          of connecting.
        </p>
        <Button asChild variant="outline" className="mt-4 min-h-[48px]">
          <Link to="/secure-within">Explore Secure Within</Link>
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Want to revisit an exercise? Every day stays open — pick any of them from{" "}
        <Link to="/breaking-free" className="underline">
          the 30 days
        </Link>
        .
      </p>
    </div>
  );
}
